import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
  useMemo,
} from 'react';
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import { Theme } from './themesTypes';
import { themes } from './themeStyle';
import { createMuiTheme } from './muiTheme';
import { LOG } from '../../../common/Utilities';

import '@fontsource/manrope';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'theme';

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [currentTheme, setCurrentTheme] = useState<Theme>(() => {
    try {
      const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
      return savedTheme ? JSON.parse(savedTheme) : themes.light;
    } catch {
      localStorage.removeItem(THEME_STORAGE_KEY);
      return themes.light;
    }
  });

  // Load saved theme from localStorage
  useEffect(() => {
    if ((global as any).me?.email !== 'admin-paiv@gogamify.com') {
      setCurrentTheme(themes.light);
      return;
    }
    const loadTheme = () => {
      try {
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme) {
          const parsedTheme = JSON.parse(savedTheme);
          setCurrentTheme(parsedTheme);
          LOG.debug('Theme set to:', parsedTheme);
        }
      } catch (error) {
        console.error('Failed to load theme:', error);
      }
    };
    loadTheme();
  }, []);

  // Toggle theme and persist selection
  const toggleTheme = () => {
    setCurrentTheme((prevTheme) => {
      const newTheme = prevTheme.isDark ? themes.light : themes.dark;
      localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(newTheme));
      return newTheme;
    });
  };

  // Create the MUI theme using our custom theme
  const muiTheme = useMemo(() => createMuiTheme(currentTheme), [currentTheme]);

  return (
    <ThemeContext.Provider value={{ theme: currentTheme, toggleTheme }}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
